import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// ----------------------------------------------------------------------
//   Slice del módulo Reseñas (bloque D onboarding v2 — add-on de pago).
//   Estado plano — el gate del add-on se maneja en las thunks (402 →
//   `needsAddOn`).
// ----------------------------------------------------------------------

const initialState = {
  loading: false,
  error: null,
  needsAddOn: false,        // true si backend devolvió 402

  reviews: [],
  reviewsTotal: 0,
  reviewsFilters: { status: null, platform: null, rating: null, page: 0, pageSize: 25 },

  current: null,            // detalle seleccionado

  templates: [],
  templatesLoading: false,

  dashboard: null,
  dashboardLoading: false,

  settings: null,
  settingsLoading: false,
};

const slice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    startLoading(s) { s.loading = true; s.error = null; },
    setError(s, a) { s.error = a.payload; s.loading = false; },
    setNeedsAddOn(s, a) { s.needsAddOn = a.payload; s.loading = false; },

    setReviews(s, a) {
      s.reviews = a.payload.items;
      s.reviewsTotal = a.payload.total;
      s.loading = false;
      s.needsAddOn = false;
    },
    updateFilter(s, a) { s.reviewsFilters = { ...s.reviewsFilters, ...a.payload }; },

    setCurrent(s, a) { s.current = a.payload; },
    patchCurrent(s, a) { if (s.current && s.current.id === a.payload.id) s.current = a.payload; },

    setTemplates(s, a) { s.templates = a.payload; s.templatesLoading = false; },
    setTemplatesLoading(s, a) { s.templatesLoading = a.payload; },

    setDashboard(s, a) { s.dashboard = a.payload; s.dashboardLoading = false; },
    setDashboardLoading(s, a) { s.dashboardLoading = a.payload; },

    setSettings(s, a) { s.settings = a.payload; s.settingsLoading = false; },
    setSettingsLoading(s, a) { s.settingsLoading = a.payload; },
    /** Setter directo tras leer settings.hasAddOn — evita disparar
     *  fetchReviews/Dashboard/Templates que devolverían 402 y ensucian
     *  la consola. */
    setHasAddOn(s, a) { s.needsAddOn = !a.payload; },
  },
});

export default slice.reducer;
export const { updateFilter } = slice.actions;

// ─── Interceptor 402 ──────────────────────────────────────────────────

function handleGate(err, dispatch) {
  if (err?.response?.status === 402) {
    dispatch(slice.actions.setNeedsAddOn(true));
    return true;
  }
  return false;
}

// ─── Thunks ───────────────────────────────────────────────────────────

export function fetchReviews() {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.startLoading());
      const { status, platform, rating, page, pageSize } = getState().reviews.reviewsFilters;
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (platform) params.set("platform", platform);
      if (rating) params.set("rating", rating);
      params.set("page", page);
      params.set("pageSize", pageSize);
      const res = await axios.get(`reviews?${params.toString()}`);
      dispatch(slice.actions.setReviews(res.data));
    } catch (err) {
      if (!handleGate(err, dispatch)) dispatch(slice.actions.setError(err?.response?.data?.message || err.message));
    }
  };
}

export function fetchReview(id) {
  return async (dispatch) => {
    try {
      const res = await axios.get(`reviews/${id}`);
      dispatch(slice.actions.setCurrent(res.data));
      return res.data;
    } catch (err) {
      if (!handleGate(err, dispatch)) dispatch(slice.actions.setError(err?.response?.data?.message || err.message));
    }
  };
}

export function replyReview(id, text, tone) {
  return async (dispatch) => {
    const res = await axios.post(`reviews/${id}/reply`, { text, tone });
    dispatch(slice.actions.patchCurrent(res.data));
    return res.data;
  };
}

export function regenerateDraft(id, tone) {
  return async (dispatch) => {
    const res = await axios.post(`reviews/${id}/regenerate-draft`, { tone });
    dispatch(slice.actions.patchCurrent(res.data));
    return res.data;
  };
}

export function bulkApproveDrafts(ids) {
  return async (dispatch) => {
    const res = await axios.post(`reviews/bulk-approve-drafts`, ids);
    dispatch(fetchReviews());
    return res.data;
  };
}

export function logInternalAction(id, action, note) {
  return async () => {
    await axios.post(`reviews/${id}/internal-action`, { action, note });
  };
}

export function fetchTemplates() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setTemplatesLoading(true));
      const res = await axios.get("reviews/templates");
      dispatch(slice.actions.setTemplates(res.data));
    } catch (err) {
      if (!handleGate(err, dispatch)) dispatch(slice.actions.setError(err?.response?.data?.message || err.message));
      dispatch(slice.actions.setTemplatesLoading(false));
    }
  };
}

export function upsertTemplate(dto) {
  return async (dispatch) => {
    const res = dto.id
      ? await axios.put(`reviews/templates/${dto.id}`, dto)
      : await axios.post(`reviews/templates`, dto);
    dispatch(fetchTemplates());
    return res.data;
  };
}

export function deleteTemplate(id) {
  return async (dispatch) => {
    await axios.delete(`reviews/templates/${id}`);
    dispatch(fetchTemplates());
  };
}

export function fetchDashboard(from, to) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setDashboardLoading(true));
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await axios.get(`reviews/stats?${params.toString()}`);
      dispatch(slice.actions.setDashboard(res.data));
    } catch (err) {
      if (!handleGate(err, dispatch)) dispatch(slice.actions.setError(err?.response?.data?.message || err.message));
      dispatch(slice.actions.setDashboardLoading(false));
    }
  };
}

export function fetchReviewSettings() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setSettingsLoading(true));
      const res = await axios.get("reviews/settings");
      dispatch(slice.actions.setSettings(res.data));
      // Canario del gate: si el user no tiene el add-on activo, marca
      // needsAddOn=true YA y las thunks que cargan bandeja/dashboard/
      // templates se saltan (evita 3× 402 en la consola).
      dispatch(slice.actions.setHasAddOn(!!res.data?.hasAddOn));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.setError(err?.response?.data?.message || err.message));
      dispatch(slice.actions.setSettingsLoading(false));
    }
  };
}

export function saveReviewSettings(settings) {
  return async (dispatch) => {
    try {
      await axios.put("reviews/settings", settings);
      dispatch(slice.actions.setSettings(settings));
    } catch (err) {
      if (!handleGate(err, dispatch)) throw err;
    }
  };
}
